import { LEGACY_TO_BROWSER_WORLD_SCALE } from './LegacyCameraRig';

export type LegacyThemeEnvironment = Readonly<{
  id: number;
  terrainTexture: string;
  terrainMaterialColors: LegacyTerrainMaterialColors;
  waterTexture: string;
  waterVisible: boolean;
  shoreline: boolean;
  shorelineColor: readonly [number, number, number];
  ambientColor: readonly [number, number, number];
  bloom: LegacyBloomSettings;
  fogColor: readonly [number, number, number];
  fogNear: number;
  fogFar: number;
}>;

export type LegacyTerrainMaterialColors = readonly [
  readonly [number, number, number],
  readonly [number, number, number],
  readonly [number, number, number],
];

export type LegacyBloomSettings = Readonly<{
  blurSize: number;
  intensity: number;
  exposureCutoff: number;
}>;

type ThemeDefinition = Omit<
  LegacyThemeEnvironment,
  'id' | 'terrainMaterialColors' | 'fogNear' | 'fogFar' | 'bloom'
> &
  Readonly<{ fogStart: number; fogEnd: number }>;

const BLOOM_DEFAULT: LegacyBloomSettings = Object.freeze({
  blurSize: 0.01,
  intensity: 0.7,
  exposureCutoff: 0.85,
});

const BLOOM_BY_THEME: Readonly<Record<number, LegacyBloomSettings>> = {
  0: bloom(0.01, 0.5, 0.9),
  1: bloom(0.01, 0.2, 0.94),
  2: bloom(0.01, 0.5, 0.9),
  3: bloom(0.01, 0.5, 0.9),
  5: bloom(0.01, 0.5, 0.9),
  6: bloom(0.01, 0.6, 0.9),
  7: bloom(0.01, 0.55, 0.85),
  8: BLOOM_DEFAULT,
  9: bloom(0.01, 0.75, 0.88),
  10: bloom(0.03, 2, 0.6),
  11: bloom(0.01, 0.4, 0.95),
  12: bloom(0.01, 0.75, 0.88),
  50: BLOOM_DEFAULT,
  51: BLOOM_DEFAULT,
  52: bloom(0.01, 0.5, 0.9),
  53: bloom(0.014, 0.85, 0.75),
};

// resources.xml tintred/green/blue 1..3, normalized exactly as agH's ColorRGBA catalog.
// azw.aa() samples these active TileTerrain material colors for each building footprint.
const TERRAIN_MATERIAL_COLORS_BY_THEME: Readonly<Record<number, LegacyTerrainMaterialColors>> = {
  0: terrainColors([60, 105, 0], [206, 178, 139], [69, 52, 24]),
  1: terrainColors([150, 150, 150], [124, 112, 107], [240, 240, 240]),
  2: terrainColors([60, 105, 0], [206, 178, 139], [69, 52, 24]),
  3: terrainColors([60, 105, 0], [206, 178, 139], [69, 52, 24]),
  5: terrainColors([120, 117, 4], [162, 148, 148], [69, 52, 24]),
  6: terrainColors([54, 68, 58], [116, 115, 126], [53, 50, 51]),
  7: terrainColors([206, 178, 139], [206, 178, 139], [206, 178, 139]),
  8: terrainColors([122, 122, 122], [122, 122, 122], [122, 122, 122]),
  9: terrainColors([107, 138, 35], [180, 158, 128], [107, 138, 35]),
  10: terrainColors([58, 70, 58], [53, 70, 53], [53, 70, 51]),
  11: terrainColors([206, 178, 139], [206, 178, 139], [206, 178, 139]),
  12: terrainColors([40, 160, 160], [40, 160, 160], [40, 160, 160]),
  50: terrainColors([60, 105, 0], [206, 178, 139], [69, 52, 24]),
  51: terrainColors([100, 58, 58], [116, 115, 126], [53, 50, 51]),
  53: terrainColors([17, 27, 43], [132, 131, 134], [17, 27, 43]),
  52: terrainColors([100, 114, 141], [100, 114, 141], [100, 114, 141]),
};

const THEMES: Readonly<Record<number, ThemeDefinition>> = {
  0: theme(
    'tileset0.jpg',
    'water2.png',
    true,
    [0.6, 0.54, 0.47],
    [0.7, 0.7, 0.7],
    [1, 1, 1],
    100,
    210,
  ),
  1: theme(
    'tileset2.jpg',
    'water1.png',
    true,
    [0.246, 0.222, 0.215],
    [0.7, 0.7, 0.75],
    [1, 1, 1],
    60,
    160,
  ),
  2: theme(
    'tileset0.jpg',
    'water2.png',
    true,
    [0.6, 0.54, 0.47],
    [0.7, 0.7, 0.7],
    [1, 1, 1],
    100,
    210,
  ),
  3: theme(
    'tileset4.jpg',
    'water3.png',
    true,
    [0.46, 0.65, 0.72],
    [0.7, 0.7, 0.7],
    [0.8, 0.8, 0.8],
    120,
    240,
  ),
  5: theme(
    'tileset1.jpg',
    'water5.png',
    false,
    [0.58, 0.45, 0.27],
    [0.7, 0.7, 0.7],
    [0.8, 0.7, 0.6],
    90,
    200,
  ),
  6: theme(
    'tileset3.jpg',
    'water4.png',
    true,
    [0.28, 0.27, 0.3],
    [1, 1, 1],
    [0.5, 0.5, 0.6],
    60,
    160,
  ),
  7: theme(
    'tileset7.jpg',
    'water2.png',
    true,
    [0.6, 0.54, 0.47],
    [0.75, 0.7, 0.7],
    [1, 1, 1],
    75,
    200,
  ),
  8: theme(
    'tileset8.jpg',
    'water6.png',
    true,
    [0.6, 0.54, 0.47],
    [0.75, 0.7, 0.7],
    [1, 1, 1],
    200,
    700,
  ),
  9: theme(
    'tileset9.jpg',
    'water2.png',
    true,
    [0.6, 0.54, 0.47],
    [0.8, 0.72, 0.75],
    [1, 1, 1],
    100,
    210,
  ),
  10: theme(
    'tileset10.jpg',
    'water4.png',
    true,
    [0.28, 0.27, 0.3],
    [0.7, 0.7, 0.6],
    [0.2, 0.8, 0.2],
    40,
    150,
  ),
  11: theme(
    'tileset7.jpg',
    'water2.png',
    true,
    [0.6, 0.54, 0.47],
    [0.1, 0.1, 0.2],
    [0.2, 0.2, 0.3],
    40,
    120,
  ),
  12: theme(
    'tileset11.jpg',
    'water2.png',
    false,
    [0.4, 0.6, 0.6],
    [0.2, 0.85, 0.85],
    [0.2, 0.7, 1],
    40,
    80,
    false,
  ),
  50: theme(
    'blacktiles.jpg',
    'water3.png',
    false,
    [0.46, 0.65, 0.72],
    [1, 1, 1],
    [0, 0, 0],
    120,
    240,
  ),
  51: theme(
    'tileset6.jpg',
    'water4.png',
    true,
    [0.28, 0.27, 0.3],
    [0.7, 0.6, 0.6],
    [0.7, 0.2, 0.2],
    40,
    140,
  ),
  53: theme(
    'tileset_circuitboard.jpg',
    'water4.png',
    true,
    [0.08, 0.65, 0.8],
    [0.16, 0.25, 0.45],
    [0.035, 0.08, 0.18],
    70,
    220,
  ),
  52: theme(
    'tileset_rainbow.jpg',
    'rainbow_water.png',
    true,
    [0.46, 0.65, 0.72],
    [0.7, 0.7, 0.7],
    [1, 1, 1],
    100,
    170,
  ),
};

export function legacyThemeEnvironment(id: number): LegacyThemeEnvironment {
  const definition = THEMES[id] ?? THEMES[0];
  if (definition === undefined) throw new Error('The summer theme definition is missing.');
  const resolvedId = THEMES[id] === undefined ? 0 : id;
  return {
    id: resolvedId,
    terrainTexture: definition.terrainTexture,
    terrainMaterialColors:
      TERRAIN_MATERIAL_COLORS_BY_THEME[resolvedId] ??
      requiredTerrainMaterialColors(TERRAIN_MATERIAL_COLORS_BY_THEME[0]),
    waterTexture: definition.waterTexture,
    waterVisible: definition.waterVisible,
    shoreline: definition.shoreline,
    shorelineColor: definition.shorelineColor,
    ambientColor: definition.ambientColor,
    bloom: BLOOM_BY_THEME[resolvedId] ?? BLOOM_DEFAULT,
    fogColor: definition.fogColor,
    fogNear: definition.fogStart * LEGACY_TO_BROWSER_WORLD_SCALE,
    fogFar: definition.fogEnd * LEGACY_TO_BROWSER_WORLD_SCALE,
  };
}

function terrainColors(
  base: readonly [number, number, number],
  sand: readonly [number, number, number],
  filth: readonly [number, number, number],
): LegacyTerrainMaterialColors {
  const normalize = (color: readonly [number, number, number]) =>
    color.map((component) => component / 255) as [number, number, number];
  return [normalize(base), normalize(sand), normalize(filth)];
}

function requiredTerrainMaterialColors(
  value: LegacyTerrainMaterialColors | undefined,
): LegacyTerrainMaterialColors {
  if (value === undefined) throw new Error('The summer terrain tint catalog is missing.');
  return value;
}

function bloom(blurSize: number, intensity: number, exposureCutoff: number): LegacyBloomSettings {
  return Object.freeze({ blurSize, intensity, exposureCutoff });
}

function theme(
  terrainTexture: string,
  waterTexture: string,
  shoreline: boolean,
  shorelineColor: readonly [number, number, number],
  ambientColor: readonly [number, number, number],
  fogColor: readonly [number, number, number],
  fogStart: number,
  fogEnd: number,
  waterVisible = true,
): ThemeDefinition {
  return {
    terrainTexture,
    waterTexture,
    waterVisible,
    shoreline,
    shorelineColor,
    ambientColor,
    fogColor,
    fogStart,
    fogEnd,
  };
}
